/*
  Klientide tagasiside — üks tsitaat korraga (sections/client-voices.liquid).

  Kaks teed sama sihini:
    · LIBISTUS / puuteplaat — natiivne `scroll-snap`; nähtava ülesulandumise
      teeb CSS (scroll-driven, slaidi asukoha järgi). See fail ainult loeb
      kerimisest, milline tsitaat on ees, ja uuendab nooli.
    · NOOLED — kerimine hüppab HETKEGA ja nähtav üleminek on ajastatud
      ülesulandumine (WAAPI): välja 140 ms, sisse 280 ms, tugev ease-out,
      12 px nihe liikumise suunas. Natiivne sujuv kerimine liigutaks 1300 px
      laiust teksti üle ekraani brauseri enda kõveraga; see on lühem, vaiksem
      ja suunaga (järgmine tuleb paremalt). Vähendatud liikumisega jääb alles
      ainult läbipaistvus.

  Ilma requestAnimationFrame'ita meelega: rAF ei käivitu peidetud paanis
  (CLAUDE.md mõõtmislõksud) ja siin pole teda vaja.
*/
if (!customElements.get('client-voices')) {
  const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

  customElements.define(
    'client-voices',
    class ClientVoices extends HTMLElement {
      connectedCallback() {
        // „Loe pikemalt" (20.10.2026) — enne karusselli varajast väljumist,
        // sest tootelehel võib olla ainult üks tsitaat. Avamine on ühesuunaline:
        // „Näita vähem" nupu lükkas klient 27.08.2026 tootelehe auhindadel
        // tagasi („kole button"), ja kokku tõmbuv kast liigutaks nooled
        // sõrme alt ära.
        this.expanders = Array.from(this.querySelectorAll('[data-cv-expand]'));
        this.onExpand = (event) => this.expand(event.currentTarget);
        this.expanders.forEach((button) => button.addEventListener('click', this.onExpand));

        this.track = this.querySelector('[data-cv-track]');
        this.slides = Array.from(this.querySelectorAll('[data-cv-slide]'));
        this.prev = this.querySelector('[data-cv-prev]');
        this.next = this.querySelector('[data-cv-next]');
        this.status = this.querySelector('[data-cv-status]');
        if (!this.track || this.slides.length < 2) return;

        this.current = 0;
        this.pending = null;
        this.token = 0;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.onScroll = this.onScroll.bind(this);
        this.onScrollEnd = this.onScrollEnd.bind(this);
        this.onPrev = () => this.go(this.current - 1);
        this.onNext = () => this.go(this.current + 1);
        this.onBlockSelect = this.onBlockSelect.bind(this);

        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.track.addEventListener('scrollend', this.onScrollEnd);
        this.prev?.addEventListener('click', this.onPrev);
        this.next?.addEventListener('click', this.onNext);
        document.addEventListener('shopify:block:select', this.onBlockSelect);

        this.setCurrent(this.indexFromScroll() ?? 0);
        this.classList.add('is-ready');
      }

      disconnectedCallback() {
        this.expanders?.forEach((button) => button.removeEventListener('click', this.onExpand));
        if (!this.track) return;
        this.track.removeEventListener('scroll', this.onScroll);
        this.track.removeEventListener('scrollend', this.onScrollEnd);
        this.prev?.removeEventListener('click', this.onPrev);
        this.next?.removeEventListener('click', this.onNext);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        this.anim?.cancel();
      }

      // Nupp kaob ja fookus läheb äsja ilmunud tekstile — muidu kukuks ta
      // `body`-le ja ekraanilugeja ei teaks, kust jätkata.
      expand(button) {
        const rest = document.getElementById(button.getAttribute('aria-controls'));
        if (!rest) return;
        rest.hidden = false;
        button.setAttribute('aria-expanded', 'true');
        button.hidden = true;
        rest.focus({ preventScroll: true });
      }

      indexFromScroll() {
        const width = this.track.clientWidth;
        if (!width) return null;
        return Math.round(this.track.scrollLeft / width);
      }

      onScroll() {
        const index = this.indexFromScroll();
        if (index === null) return;
        if (this.pending !== null) {
          if (index === this.pending) this.pending = null;
          return;
        }
        if (index !== this.current) this.setCurrent(index);
      }

      onScrollEnd() {
        this.pending = null;
        const index = this.indexFromScroll();
        if (index !== null && index !== this.current) this.setCurrent(index);
      }

      // Theme Editor: plokki valides näitab karussell seda tsitaati.
      onBlockSelect(event) {
        const index = this.slides.indexOf(event.target);
        if (index >= 0) this.go(index, { instant: true });
      }

      jump(index) {
        this.pending = index;
        this.track.scrollTo({ left: index * this.track.clientWidth, behavior: 'instant' });
      }

      async go(index, { instant = false } = {}) {
        const target = Math.max(0, Math.min(index, this.slides.length - 1));
        if (target === this.current) return;
        const direction = target > this.current ? 1 : -1;
        const token = ++this.token;

        this.setCurrent(target, { announce: !instant });

        const canAnimate = !instant && typeof this.track.animate === 'function';
        if (!canAnimate) {
          this.jump(target);
          return;
        }

        const reduce = this.reducedMotion.matches;
        const shift = reduce ? 0 : 12;
        const blur = reduce ? 0 : 2;
        const rest = { opacity: 1, filter: 'blur(0px)', transform: 'translateX(0)' };

        // Kiire teine klõps: eelmine üleminek katkeb, väljaminekut ei
        // korrata — hüppa ja tule kohe sisse.
        const busy = this.anim && this.anim.playState === 'running';
        this.anim?.cancel();

        if (!busy) {
          const out = this.track.animate(
            [rest, { opacity: 0, filter: `blur(${blur}px)`, transform: `translateX(${-direction * shift}px)` }],
            { duration: reduce ? 120 : 140, easing: EASE_OUT, fill: 'forwards' }
          );
          this.anim = out;
          try {
            await out.finished;
          } catch (error) {
            return; // uuem klõps võttis üle
          }
          if (token !== this.token) return;
        }

        this.jump(target);
        const enter = this.track.animate(
          [{ opacity: 0, filter: `blur(${blur}px)`, transform: `translateX(${direction * shift}px)` }, rest],
          { duration: reduce ? 160 : 280, easing: EASE_OUT }
        );
        this.anim?.cancel();
        this.anim = enter;
      }

      setCurrent(index, { announce = false } = {}) {
        this.current = index;
        const last = this.slides.length - 1;

        // Kui fookus on noolel, mis just keelatuks läheb (viimasel tsitaadil
        // „järgmine"), viiakse ta teisele noolele — muidu kukuks fookus
        // `body`-le ja klaviatuurikasutaja kaotaks koha.
        const active = document.activeElement;
        if (this.prev) this.prev.disabled = index <= 0;
        if (this.next) this.next.disabled = index >= last;
        if (active === this.next && this.next.disabled) this.prev?.focus();
        if (active === this.prev && this.prev.disabled) this.next?.focus();

        if (announce && this.status) {
          const name = this.slides[index].querySelector('figcaption')?.textContent.trim();
          this.status.textContent = `Tsitaat ${index + 1} / ${this.slides.length}${name ? `, ${name}` : ''}`;
        }
      }
    }
  );
}
